import { useEffect, useId, useRef, useState, type ChangeEvent, type FocusEvent, type ReactNode } from 'react';
import { applyMask, digitsOf, formatNumber, maskAttrs, parseNumber, type MaskKind } from '../masks.ts';
import { Input, type InputProps } from './Field.tsx';

/**
 * Input com máscara fixa (telefone, CPF/CNPJ, CEP, cartão, validade, CVV, código de 6 dígitos).
 * Funciona controlado (`value`) ou com `register` do react-hook-form: o valor no DOM já sai formatado e vai assim para a API
 * (que normaliza; os validadores só olham dígitos). Ao ganhar foco também formata o que veio pronto do servidor.
 */
export interface MaskedInputProps extends Omit<InputProps, 'value' | 'inputMode'> { mask: MaskKind; value?: string | null }
export function MaskedInput({ mask, value, onChange, onFocus, placeholder, autoComplete, maxLength, type, ...rest }: MaskedInputProps) {
  const attrs = maskAttrs(mask);
  const format = (e: ChangeEvent<HTMLInputElement> | FocusEvent<HTMLInputElement>) => {
    const masked = applyMask(mask, e.target.value);
    if (masked !== e.target.value) e.target.value = masked;
  };
  return (
    <Input
      {...rest}
      type={type ?? (mask === 'phone' ? 'tel' : 'text')}
      inputMode={attrs.inputMode}
      autoComplete={autoComplete ?? attrs.autoComplete}
      placeholder={placeholder ?? attrs.placeholder}
      maxLength={maxLength ?? attrs.maxLength}
      {...(value !== undefined ? { value: applyMask(mask, value ?? '') } : {})}
      onChange={(e) => { format(e); onChange?.(e); }}
      onFocus={(e) => { format(e); onFocus?.(e); }}
    />
  );
}

/** Valor numérico a partir de um campo mascarado (dígitos). */
export const unmasked = digitsOf;

interface NumberBase extends Omit<InputProps, 'value' | 'onChange' | 'type' | 'inputMode' | 'prefix'> {
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  locale?: string;
  /** Texto fixo antes/depois do número (R$, %, min, km…). */
  prefix?: ReactNode;
  suffix?: ReactNode;
  min?: number;
  max?: number;
}

/**
 * Campo numérico com separadores do idioma e prefixo/sufixo visual (R$, %, min…). Aceita vírgula ou ponto ao digitar;
 * formata ao sair do campo. `decimals` fixa as casas na saída; `maxDecimals` limita o que pode ser digitado.
 */
export interface NumberInputProps extends NumberBase { decimals?: number; maxDecimals?: number; allowNegative?: boolean }
export function NumberInput({ value, onChange, locale = typeof navigator !== 'undefined' ? navigator.language : 'pt-BR', decimals = 0, maxDecimals = decimals, allowNegative = false, prefix, suffix, min, max, onBlur, onFocus, label, hint, error, id, className = '', style, ...rest }: NumberInputProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const decSep = new Intl.NumberFormat(locale).format(1.5).replace(/\d/g, '');
  const toText = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? '' : formatNumber(v, locale, decimals, maxDecimals));
  const [text, setText] = useState(() => toText(value));
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setText(toText(value)); }, [value, locale, decimals]);
  const clamp = (n: number | null) => (n == null ? null : Math.min(max ?? Infinity, Math.max(min ?? -Infinity, n)));
  const change = (e: ChangeEvent<HTMLInputElement>) => {
    // Só dígitos, um separador decimal (vírgula ou ponto) e sinal quando permitido.
    let t = e.target.value.replace(allowNegative ? /[^\d.,-]/g : /[^\d.,]/g, '');
    if (maxDecimals === 0) t = t.replace(/[.,]/g, '');
    else { const first = t.search(/[.,]/); if (first >= 0) { const frac = t.slice(first + 1).replace(/[.,]/g, '').slice(0, maxDecimals); t = `${t.slice(0, first)}${decSep}${frac}`; } }
    setText(t);
    onChange(t === '' || t === '-' ? null : parseNumber(t, locale));
  };
  return (
    <div className={`k-field ${error ? 'k-field--error' : ''} ${className}`} style={style}>
      {label && <label className="k-field__label" htmlFor={inputId}>{label}</label>}
      <div className={`k-adorn ${prefix ? 'k-adorn--prefix' : ''} ${suffix ? 'k-adorn--suffix' : ''}`}>
        {prefix && <span className="k-adorn__prefix" aria-hidden>{prefix}</span>}
        <input
          id={inputId}
          className="k-input"
          type="text"
          inputMode={maxDecimals > 0 ? 'decimal' : 'numeric'}
          aria-invalid={!!error}
          value={text}
          onChange={change}
          onFocus={(e) => { focused.current = true; onFocus?.(e); }}
          onBlur={(e) => { focused.current = false; const n = clamp(parseNumber(text, locale)); if (n !== (value ?? null)) onChange(n); setText(toText(n)); onBlur?.(e); }}
          {...rest}
        />
        {suffix && <span className="k-adorn__suffix" aria-hidden>{suffix}</span>}
      </div>
      {error ? <div className="k-field__error">{error}</div> : hint ? <div className="k-field__hint">{hint}</div> : null}
    </div>
  );
}

/** Dinheiro: digita-se como em caixa (centavos entram pela direita) e mostra sempre R$ 1.234,56. */
export interface MoneyInputProps extends NumberBase { currency?: string; decimals?: number }
export function MoneyInput({ value, onChange, locale = typeof navigator !== 'undefined' ? navigator.language : 'pt-BR', currency = 'BRL', decimals = 2, prefix, min, max, onBlur, onFocus, label, hint, error, id, className = '', style, ...rest }: MoneyInputProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const symbol = prefix ?? new Intl.NumberFormat(locale, { style: 'currency', currency }).formatToParts(0).find((p) => p.type === 'currency')?.value ?? currency;
  const factor = 10 ** decimals;
  const toText = (v: number | null | undefined) => (v == null || Number.isNaN(v) ? '' : formatNumber(v, locale, decimals));
  const [text, setText] = useState(() => toText(value));
  const focused = useRef(false);
  useEffect(() => { if (!focused.current) setText(toText(value)); }, [value, locale, decimals]);
  const change = (e: ChangeEvent<HTMLInputElement>) => {
    const d = digitsOf(e.target.value).replace(/^0+(?=\d)/, '').slice(0, 15);
    if (!d) { setText(''); onChange(null); return; }
    let n = Number(d) / factor;
    if (max != null) n = Math.min(max, n);
    if (min != null) n = Math.max(min, n);
    setText(toText(n));
    onChange(n);
  };
  return (
    <div className={`k-field ${error ? 'k-field--error' : ''} ${className}`} style={style}>
      {label && <label className="k-field__label" htmlFor={inputId}>{label}</label>}
      <div className="k-adorn k-adorn--prefix">
        <span className="k-adorn__prefix" aria-hidden>{symbol}</span>
        <input id={inputId} className="k-input" type="text" inputMode="decimal" aria-invalid={!!error} value={text} placeholder={toText(0)} onChange={change} onFocus={(e) => { focused.current = true; onFocus?.(e); }} onBlur={(e) => { focused.current = false; onBlur?.(e); }} {...rest} />
      </div>
      {error ? <div className="k-field__error">{error}</div> : hint ? <div className="k-field__hint">{hint}</div> : null}
    </div>
  );
}

/**
 * NumberInput que só entrega o valor ao sair do campo ou com Enter (`onCommit`), e só se for um número válido dentro
 * de min/max e diferente do atual. Para configurações salvas direto na API, sem uma chamada por tecla.
 */
export interface CommitNumberInputProps extends Omit<NumberInputProps, 'onChange'> { onCommit: (v: number) => void }
export function CommitNumberInput({ value, onCommit, min, max, onBlur, onKeyDown, ...rest }: CommitNumberInputProps) {
  const [draft, setDraft] = useState<number | null>(value ?? null);
  useEffect(() => { setDraft(value ?? null); }, [value]);
  const commit = () => {
    if (draft == null || Number.isNaN(draft)) { setDraft(value ?? null); return; }
    if ((min != null && draft < min) || (max != null && draft > max)) { setDraft(value ?? null); return; }
    if (draft !== (value ?? null)) onCommit(draft);
  };
  return <NumberInput {...rest} min={min} max={max} value={draft} onChange={setDraft} onBlur={(e) => { commit(); onBlur?.(e); }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); } onKeyDown?.(e); }} />;
}
